(() => {
  'use strict';

  if (window.__nutPdpCartShippingLoaded) return;
  window.__nutPdpCartShippingLoaded = true;

  const ROOT_SELECTOR = '[data-nut-pdp-cart-shipping]';
  const CART_MUTATION_RE = /\/cart\/(add|change|update|clear)(?:\.js)?(?:\?|$)/i;
  const CART_GET_RE = /\/cart(?:\.js)?(?:\?|$)/i;
  const routeRoot = window.Shopify?.routes?.root || '/';
  const cartUrl = `${routeRoot}cart.js`;
  const nativeFetch = window.fetch.bind(window);

  let lastCart = null;
  let syncTimer = 0;
  let syncVersion = 0;
  let optimisticVersion = 0;

  const roots = () => Array.from(document.querySelectorAll(ROOT_SELECTOR));

  const parseNumber = (value, fallback = 0) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  };

  const formatMoney = (cents, root) => {
    const moneyFormat = root.dataset.moneyFormat || '{{amount}}';

    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(Math.round(cents), moneyFormat);
    }

    const currency = root.dataset.currency || 'EGP';
    const locale = document.documentElement.lang || 'en';

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency
      }).format(cents / 100);
    } catch (_error) {
      return `${currency} ${(cents / 100).toFixed(2)}`;
    }
  };

  const renderRoot = (root, totalCents, state = 'exact') => {
    const threshold = parseNumber(root.dataset.threshold, 99900);
    const safeTotal = Math.max(0, parseNumber(totalCents, 0));
    const remaining = Math.max(0, threshold - safeTotal);
    const progress = threshold > 0
      ? Math.min(100, Math.max(0, (safeTotal / threshold) * 100))
      : 100;

    const message = root.querySelector('[data-nut-pdp-cart-shipping-message]');
    const fill = root.querySelector('[data-nut-pdp-cart-shipping-fill]');
    const track = fill?.closest('[role="progressbar"]');

    root.dataset.state = state;
    root.classList.toggle('is-unlocked', remaining <= 0);
    root.classList.toggle('is-optimistic', state === 'optimistic');

    if (message) {
      if (remaining <= 0) {
        const unlocked = root.dataset.unlockedText || 'Free delivery unlocked';
        message.innerHTML = '';
        const strong = document.createElement('strong');
        strong.textContent = unlocked;
        message.appendChild(strong);
      } else {
        const template = root.dataset.remainingTemplate || 'Add [amount] for free delivery';
        message.textContent = template.replace('[amount]', formatMoney(remaining, root));
      }
    }

    if (fill) {
      fill.style.width = `${progress}%`;
    }

    if (track) {
      track.setAttribute('aria-valuenow', String(Math.round(progress)));
    }
  };

  const renderAll = (totalCents, state = 'exact') => {
    roots().forEach((root) => renderRoot(root, totalCents, state));
  };

  const initialTotal = () => {
    const root = roots()[0];
    return parseNumber(root?.dataset.initialTotal, 0);
  };

  const currentExactTotal = () => {
    return parseNumber(lastCart?.total_price, initialTotal());
  };

  const getUrlString = (input) => {
    if (typeof input === 'string') return input;
    if (input instanceof URL) return input.href;
    if (input && typeof input.url === 'string') return input.url;
    return '';
  };

  const syncFromServer = async () => {
    const version = ++syncVersion;

    try {
      const response = await nativeFetch(cartUrl, {
        method: 'GET',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json'
        }
      });

      if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);

      const cart = await response.json();
      if (version !== syncVersion) return;

      lastCart = cart;
      optimisticVersion += 1;
      renderAll(cart.total_price, 'exact');
    } catch (_error) {
      renderAll(currentExactTotal(), 'exact');
    }
  };

  const queueSync = (delay = 30) => {
    window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(syncFromServer, delay);
  };

  const getSelectedVariantPrice = (form, shippingRoot) => {
    const variantInput = form?.querySelector('[name="id"]');
    const variantId = parseNumber(variantInput?.value, 0);
    const variantsScript = shippingRoot?.querySelector('[data-nut-pdp-cart-shipping-variants]');

    if (!variantId || !variantsScript) return null;

    try {
      const variants = JSON.parse(variantsScript.textContent || '[]');
      const variant = variants.find((item) => parseNumber(item.id) === variantId);
      return variant ? parseNumber(variant.price, null) : null;
    } catch (_error) {
      return null;
    }
  };

  const getOptimisticAddTotal = (form) => {
    const section = form.closest('[id^="shopify-section-"]') || document;
    const shippingRoot = section.querySelector(ROOT_SELECTOR) || roots()[0];
    if (!shippingRoot) return null;

    const selectedPack = section.querySelector(
      '[data-nut-pdp-pack-option][aria-checked="true"]'
    );

    const formQuantity = parseNumber(form.querySelector('[name="quantity"]')?.value, 1);
    const quantity = parseNumber(
      selectedPack?.dataset.quantity ?? selectedPack?.dataset.nutPdpPackQuantity,
      formQuantity
    );
    const discount = parseNumber(
      selectedPack?.dataset.discount ?? selectedPack?.dataset.nutPdpPackDiscount,
      0
    );
    const unitPrice = getSelectedVariantPrice(form, shippingRoot);

    if (unitPrice === null) return null;

    const lineTotal = Math.round(unitPrice * quantity * (1 - discount / 100));
    return currentExactTotal() + lineTotal;
  };

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const action = form.getAttribute('action') || '';
    if (!/\/cart\/add(?:\.js)?(?:\?|$)/i.test(action)) return;

    const optimisticTotal = getOptimisticAddTotal(form);
    if (optimisticTotal === null) return;

    const version = ++optimisticVersion;
    renderAll(optimisticTotal, 'optimistic');

    window.setTimeout(() => {
      if (version === optimisticVersion) queueSync(0);
    }, 1500);
  }, true);

  window.fetch = async (...args) => {
    const url = getUrlString(args[0]);
    const isCartMutation = CART_MUTATION_RE.test(url);

    try {
      const response = await nativeFetch(...args);

      if (isCartMutation) {
        if (response.ok) {
          queueSync(20);
        } else {
          optimisticVersion += 1;
          renderAll(currentExactTotal(), 'exact');
        }
      }

      return response;
    } catch (error) {
      if (isCartMutation) {
        optimisticVersion += 1;
        renderAll(currentExactTotal(), 'exact');
      }
      throw error;
    }
  };

  const nativeOpen = XMLHttpRequest.prototype.open;
  const nativeSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function(method, url, ...rest) {
    this.__nutPdpCartMutation = CART_MUTATION_RE.test(String(url || ''));
    return nativeOpen.call(this, method, url, ...rest);
  };

  XMLHttpRequest.prototype.send = function(...args) {
    if (this.__nutPdpCartMutation) {
      this.addEventListener('loadend', () => {
        if (this.status >= 200 && this.status < 400) {
          queueSync(20);
        } else {
          optimisticVersion += 1;
          renderAll(currentExactTotal(), 'exact');
        }
      }, { once: true });
    }

    return nativeSend.apply(this, args);
  };

  [
    'shopify:cart:lines-update',
    'cart:updated',
    'cart:update',
    'cart:refresh',
    'theme:cart:updated',
    'ajaxProduct:added'
  ].forEach((eventName) => {
    document.addEventListener(eventName, () => queueSync(10));
  });

  document.addEventListener('click', (event) => {
    const control = event.target.closest(
      '[data-cart-remove], [data-remove-item], [data-cart-item-remove], a[href*="/cart/change"], button[name="update"]'
    );

    if (control) queueSync(180);
  }, true);

  window.addEventListener('pageshow', () => queueSync(0));
  window.addEventListener('focus', () => queueSync(0));
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) queueSync(0);
  });

  renderAll(initialTotal(), 'exact');
  queueSync(0);
})();